import { GetServerSideProps } from 'next';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import Icon from '@mdi/react';
import Head from 'next/head';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import StreamSortDropdown, {
  SortTypes,
} from '@/components/Stream/StreamSortDropdown';
import search from '@/algolia/search';
import Description from '@/components/Search/Description';
import PostCard from '@/components/Stream/PostCard';
import { getSearchDescription } from '@/firebase/streamSSR';
import hashtagSections, {
  IHashtagSections,
  getHashtag,
  getRelatedHashtags,
} from '@/lib/hashtags';
import { IAlgoliaResults } from '@/types/Algolia';
import useTrendingTopics from '@/hooks/useTrendingTopics';
import { useSignupBanner } from '@/providers/SignupBannerProvider';
import SearchForm from '@/components/Search/SearchForm';
import SearchTabs from '@/components/Search/SearchTabs';

const POSTS_PER_PAGE = 10;

interface TrendingTag {
  slug: keyof IHashtagSections | null;
  title: string | null;
  style?: React.CSSProperties | null;
}

interface IProps {
  results: IAlgoliaResults | null;
  page: number;
  term: string;
  sortType: SortTypes;
  description: string | null;
}

const Hashtag = (props: IProps): JSX.Element => {
  const { results, sortType, description } = props;
  const router = useRouter();
  let desc = null;

  const { setMessage } = useSignupBanner();

  const { data: trendingHashtags } = useTrendingTopics();

  const [activeHashtag, setActiveHashtag] = useState<string | null>(null);
  const [trending, setTrending] = useState<TrendingTag[]>([]);

  useEffect(() => {
    document.body.classList.add('explore-body');
    setMessage('Join to explore more');

    return () => document.body.classList.remove('explore-body');
  }, [setMessage]);

  useEffect(() => {
    const getHashtagStyle = (
      hashtag: keyof IHashtagSections,
    ): React.CSSProperties | null => {
      const ht = getHashtag(hashtag);
      if (ht) {
        return { backgroundColor: ht.colour };
      }
      return null;
    };
    if (trendingHashtags && trendingHashtags.hashtags) {
      const ht: TrendingTag[] = trendingHashtags.hashtags.map(h => {
        if (h.category) {
          const result: TrendingTag = {
            slug: h.category
              .toLowerCase()
              .replace(/ /g, '-') as keyof IHashtagSections,
            title: h.category,
          };
          if (result.slug) result.style = getHashtagStyle(result.slug);
          return result;
        }
        return {
          slug: null,
          title: null,
          style: null,
        } as TrendingTag;
      });
      setTrending(ht);
    }
  }, [trendingHashtags]);

  if (description) {
    desc = JSON.parse(description);
  }

  const getRelatedTopics = (q: string) => {
    const related = getRelatedHashtags(q);
    const out = [];
    if (related && related.hashtags) {
      for (const key in related.hashtags) {
        if (related.hashtags[key]) {
          const ht = related.hashtags[key];
          out.push(
            <li key={`hashtag-${key}`} className="horizontal-scroll-item">
              <Link
                href={`/hashtag/${key}`}
                className="button is-rounded"
                style={{
                  backgroundColor: ht.colour ? ht.colour : related.colour,
                }}>
                {ht.title}
              </Link>
            </li>,
          );
        }
      }
    }
    return out;
  };

  if (results && results.hits) {
    return (
      <div id="search" className="container">
        <Head>
          <title>{`#${results.query} - Shift.ms`}</title>
          <link
            rel="canonical"
            href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/hashtag/${results.query}`}
          />
          <meta property="og:type" content="website" />
        </Head>
        <SearchForm />
        <SearchTabs />
        <div className="container is-narrow mt-4">
          <Description query={results.query} description={desc} />
        </div>
        <div className="container">
          <div className="hashtags-trending">
            <h2 className="title is-size-3 is-size-4-mobile">Topics</h2>
            <ul className="horizontal-scroll">
              {getRelatedTopics(results.query)}
              <li key="hashtags-browse-all" className="horizontal-scroll-item">
                <Link legacyBehavior href="/hashtag/[[...query]]" as="/hashtag">
                  <a className="button is-rounded">Browse all</a>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="container is-narrow">
          <div className="is-flex is-flex-direction-row is-justify-content-space-between is-align-items-center">
            <StreamSortDropdown
              sortField={
                sortType === SortTypes.NewestPosts ? 'created' : 'lastActive'
              }
              searchTerm={router.query?.query?.[0]}>
              <div className="is-font-brand-bold">
                Sort {results.exhaustiveNbHits ? '' : 'approximately '}
                {results.nbHits} results by
              </div>
            </StreamSortDropdown>
            <p className="is-font-brand-bold">
              {results.page > 0 ? `Page ${results.page + 1}` : ''}
            </p>
          </div>
          <div className="posts">
            {results.hits.map(post => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
          <nav className="pagination" role="navigation" aria-label="pagination">
            {results.page > 0 ? (
              <Link
                href={`/search/${results.query}/${
                  sortType
                    ? `${sortType}/${results.page}`
                    : `${SortTypes.RecentActivity}/${results.page}`
                }`}
                className="pagination-previous"
                title="Previous">
                Previous
              </Link>
            ) : (
              <a className="pagination-previous is-disabled" title="Previous">
                Previous
              </a>
            )}
            {results.nbPages > results.page + 1 ? (
              <Link
                href={`/search/${results.query}/${
                  sortType
                    ? `${sortType}/${results.page + 2}`
                    : `${SortTypes.RecentActivity}/${results.page + 2}`
                }`}
                className="pagination-next">
                Next page
              </Link>
            ) : (
              <a className="pagination-next is-disabled">Next page</a>
            )}
          </nav>
        </div>
      </div>
    );
  }

  const handleHashtagClick = (
    event:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>,
    key: string,
  ) => {
    event.preventDefault();
    if (activeHashtag === key) {
      setActiveHashtag(null);
    } else {
      setActiveHashtag(key);
    }
  };

  const showHashtagChildren = (key: string, children: any) => {
    const output = [];
    if (children) {
      for (const k in children) {
        if (Object.prototype.hasOwnProperty.call(children, k)) {
          output.push(
            <li key={k}>
              <Link
                legacyBehavior
                href={`/hashtag?query=${k.toLowerCase().replace(/ /g, '-')}`}
                as={`/hashtag/${k.toLowerCase().replace(/ /g, '-')}`}>
                <a
                  className="button is-rounded"
                  style={{
                    backgroundColor:
                      hashtagSections[key as keyof IHashtagSections]?.colour,
                  }}>
                  <span>{children[k].title}</span>
                </a>
              </Link>
            </li>,
          );
        }
      }
    }
    return output;
  };

  const showHashtags = () => {
    const output = [];
    for (const key in hashtagSections) {
      if (Object.prototype.hasOwnProperty.call(hashtagSections, key)) {
        output.push(
          <li key={key} className={activeHashtag === key ? 'is-active' : ''}>
            <a
              onClick={e => handleHashtagClick(e, key)}
              onKeyUp={e => handleHashtagClick(e, key)}
              role="button"
              tabIndex={0}
              className="button is-medium is-rounded is-fullwidth"
              style={{
                backgroundColor:
                  hashtagSections[key as keyof IHashtagSections]?.colour,
              }}>
              <span>
                {hashtagSections[key as keyof IHashtagSections]?.title}
              </span>
              <span className="icon">
                <Icon
                  path={activeHashtag === key ? mdiChevronUp : mdiChevronDown}
                  size={1.25}
                />
              </span>
            </a>
            {hashtagSections[key as keyof IHashtagSections]?.hashtags ? (
              <ul>
                <li>
                  <Link
                    legacyBehavior
                    href={`/hashtag?query=${key
                      .toLowerCase()
                      .replace(/ /g, '-')}`}
                    as={`/hashtag/${key.toLowerCase().replace(/ /g, '-')}`}>
                    <a
                      className="button is-rounded"
                      style={{
                        backgroundColor:
                          hashtagSections[key as keyof IHashtagSections]
                            ?.colour,
                      }}>
                      Browse all
                    </a>
                  </Link>
                </li>
                {showHashtagChildren(
                  key,
                  hashtagSections[key as keyof IHashtagSections]?.hashtags,
                )}
              </ul>
            ) : null}
          </li>,
        );
      }
    }
    return output;
  };

  return (
    <div id="search" className="container">
      <SearchForm />
      <SearchTabs />
      <div className="container">
        <div className="hashtags-trending">
          <h2 className="title is-size-3 is-size-4-mobile">Trending</h2>
          <ul className="horizontal-scroll">
            {trending &&
              trending.map(ht => (
                <React.Fragment key={ht.slug}>
                  {ht.slug ? (
                    <li key={ht.slug} className="horizontal-scroll-item">
                      <Link
                        legacyBehavior
                        href={`/hashtag?query=${ht.slug}`}
                        as={`/hashtag/${ht.slug}`}>
                        <a
                          className="button is-rounded"
                          style={ht.style ? ht.style : {}}>
                          {ht.title}
                        </a>
                      </Link>
                    </li>
                  ) : null}
                </React.Fragment>
              ))}
          </ul>
        </div>
        <ul className="hashtags-list">{showHashtags()}</ul>
      </div>
    </div>
  );
};

export default Hashtag;

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const { query: params } = query;
  let term = null;
  let sortType: string | SortTypes = SortTypes.RecentActivity;
  let pageNum = '1';
  if (params) {
    [term, sortType = SortTypes.RecentActivity, pageNum = '1'] =
      params as Array<string | SortTypes>;
  }

  const page = Number(pageNum);

  if (term) {
    const lowercaseTerm = term.toLowerCase();
    lowercaseTerm.replace(/-/g, ' ');

    let searchIndex = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_POSTS_INDEX;

    if (sortType === SortTypes.NewestPosts) {
      searchIndex = process.env
        .NEXT_PUBLIC_ALGOLIA_SEARCH_MOST_RECENT as string;
    }

    const data = await search(lowercaseTerm, searchIndex, POSTS_PER_PAGE, page);
    const desc = await getSearchDescription(lowercaseTerm);
    const description = JSON.stringify(desc);

    if (data) {
      return {
        props: {
          results: data,
          page,
          term,
          sortType,
          description,
        },
      };
    }
  }
  return {
    props: {
      results: {},
      page: 1,
      term: '',
      sortType: SortTypes.RecentActivity,
      description: null,
    },
  };
};
